/* ------------------------------ Dependencies ------------------------------ */
import { Ref, ref } from 'vue'
import { AxiosInstance } from 'axios'
/* ----------------------------- Custom Modules ----------------------------- */
import businessClient from '../clients/business'
import useAccess from 'src/hook/useAccess'
import { raiseError } from 'src/common/helpers/generator/error.helper'
/* -------------------------- Translation Variables ------------------------- */
import translate from 'src/common/helpers/language/translate'
import globalTranslateObject from 'src/translate/global.translate'
import { mainClient } from 'src/boot/axios'
/* -------------------------------------------------------------------------- */

const { canPassAccess } = useAccess()
const { t: globalT } = translate(globalTranslateObject)

function createAxiosRequest<T>(
  client: AxiosInstance,
  endpoint: string,
  payload?: Ref<Record<PropertyKey, unknown>> | Record<PropertyKey, unknown> | FormData,
  options?: Record<PropertyKey, unknown>,
) {
  const isLoadingRef = ref(false)
  const dataRef = ref({}) as Ref<T>
  const errorRef = ref(null) as Ref<Error | null>
  const request = {
    fetch: async () => {
      isLoadingRef.value = true
      return client
        .post<null, T>(endpoint, payload, options)
        .then((response) => (dataRef.value = response))
        .catch((error) => (errorRef.value = error))
        .finally(() => (isLoadingRef.value = false))
    },
    data: dataRef,
    error: errorRef,
    isLoading: isLoadingRef,
  }
  return request
}

function checkAccess(accessList: number[]) {
  if (!canPassAccess(accessList)) raiseError(globalT('access_denied'))
}

function useAxios<T>(
  endpoint: string,
  payload?: Ref<Record<PropertyKey, unknown>> | Record<PropertyKey, unknown>,
  accessList?: number[],
) {
  const client = businessClient()
  if (accessList?.length) checkAccess(accessList)
  return createAxiosRequest<T>(client, endpoint, payload)
}

function useMainAxios<T>(
  endpoint: string,
  payload?: Ref<Record<PropertyKey, unknown>> | Record<PropertyKey, unknown> | FormData,
  options?: Record<PropertyKey, unknown>,
) {
  return createAxiosRequest<T>(mainClient, endpoint, payload, options)
}

export { useAxios, useMainAxios }
